export interface BuffCrop { x: number; y: number; width: number; height: number }
const IMAGE_LIMIT = 10 * 1024 * 1024

export async function normalizeBuffImage(blob: Blob, crop?: BuffCrop): Promise<{ image: Blob; fingerprint: string }> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(blob.type)) throw new Error('PNG / JPEG / WebP')
  if (!blob.size || blob.size > IMAGE_LIMIT) throw new Error('Image exceeds 10 MB')
  const bitmap = await createImageBitmap(blob)
  try {
    const area = crop ?? { x: 0, y: 0, width: bitmap.width, height: bitmap.height }
    if (area.width < 4 || area.height < 4 || area.x < 0 || area.y < 0 || area.x + area.width > bitmap.width || area.y + area.height > bitmap.height) throw new Error('Invalid crop')
    const canvas = document.createElement('canvas')
    // Standardize storage without inventing missing pixels or stretching the aspect ratio.
    const ratio = Math.min(1, 256 / Math.max(area.width, area.height))
    canvas.width = Math.max(1, Math.round(area.width * ratio))
    canvas.height = Math.max(1, Math.round(area.height * ratio))
    const context = canvas.getContext('2d')!
    context.drawImage(bitmap, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height)
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height)
    const digest = await crypto.subtle.digest('SHA-256', pixels.data.buffer as ArrayBuffer)
    const fingerprint = `${canvas.width}x${canvas.height}:` + [...new Uint8Array(digest)].map(v => v.toString(16).padStart(2, '0')).join('')
    const image = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('Image encoding failed')), 'image/png'))
    return { image, fingerprint }
  } finally { bitmap.close() }
}

export function imageFiles(transfer: DataTransfer | null): File[] {
  return transfer ? [...transfer.files].filter(file => file.type.startsWith('image/')) : []
}
